import React from 'react';
import { TranslationFunction } from '../../components/blocks';
import { Link } from '../../components/blocks/localization';
import { ExtractionStatusIcon, TruncatedText } from '../../components/composites';
import { TableData } from '../../redux/types';
import { ExtractionSummary, SiteMapPage } from '../../types';
import { formatDateIso } from '../../utils/dateUtils';
import { toDisplayStatus } from '../../utils/extractionUtils';

export type ExtractionRow = ExtractionSummary;

interface ColumnDeps {
    getPath: (page: SiteMapPage, params?: Record<string, string>) => string;
    t: TranslationFunction;
    // Roles without access to the review page, such as poViewer, see the draft status without a link.
    canReviewDrafts: boolean;
    timezone?: string;
}

const createColumns = ({ getPath, t, canReviewDrafts, timezone }: ColumnDeps) => [
    {
        title: t('createdAt-column'),
        uniqueId: 'createdAt',
        format: (row: ExtractionRow) => formatDateIso(row.createdAt, timezone),
    },
    {
        title: t('documentType-column'),
        uniqueId: 'documentType',
        format: (row: ExtractionRow) =>
            row.documentType ? t('documentType-value', { context: row.documentType }) : '',
    },
    {
        title: t('customerReference-column'),
        uniqueId: 'customerReference',
        format: (row: ExtractionRow) => {
            const reference = row.customerReference || '';
            if (reference && toDisplayStatus(row.status) === 'CREATED' && row.documentType === 'PURCHASE_ORDER') {
                return (
                    <Link
                        type={'primary'}
                        href={getPath(SiteMapPage.viewPurchaseOrder, { purchaseOrderId: reference })}
                    >
                        {t.cognate(reference)}
                    </Link>
                );
            }
            return reference;
        },
    },
    {
        title: t('fileName-column'),
        uniqueId: 'fileName',
        format: (row: ExtractionRow) => <TruncatedText maxWidth={20} text={t.cognate(row.fileName || '')} />,
    },
    {
        title: t('requestedBy-column'),
        uniqueId: 'requestedBy',
        format: (row: ExtractionRow) => <TruncatedText maxWidth={15} text={t.cognate(row.requestedBy || '')} />,
    },
    {
        title: t('status-column'),
        uniqueId: 'status',
        format: (row: ExtractionRow) => {
            const isReviewableDraft =
                canReviewDrafts && toDisplayStatus(row.status) === 'DRAFT' && row.documentType === 'PURCHASE_ORDER';
            const statusIcon = (
                <ExtractionStatusIcon
                    status={row.status}
                    errorCode={row.errorCode}
                    extractionId={row.extractionId}
                    inheritColor={isReviewableDraft}
                />
            );
            return isReviewableDraft ? (
                <Link
                    type={'primary'}
                    href={getPath(SiteMapPage.reviewPurchaseOrderExtraction, { extractionId: row.extractionId })}
                >
                    {statusIcon}
                </Link>
            ) : (
                statusIcon
            );
        },
    },
];

export const createTableData = (extractions: ExtractionSummary[], columnDeps: ColumnDeps) =>
    new TableData(extractions, createColumns(columnDeps));
